import { ChainTransactionStatus } from '@katanaperps/katana-perps-sdk';
import React from 'react';
import styled from 'styled-components';

import { readableFillStatus } from '#/api/apiUtils';
import { ApiFill } from '#/api/types';
import LabelClickable from '#/components/basic/Button/LabelClickable';
import SimpleRoundedButton from '#/components/basic/Button/SimpleRoundedButton';
import { LabelLink } from '#/components/basic/Link/LabelLink';
import StopEventPropagationDiv from '#/components/basic/panels/StopEventPropagationDiv';
import { getBlockExplorerTransactionUrl } from '#/config/configUtils';
import { Chains } from '#/reduxState/exchange/exchangeActiveChainUtils';

import FillsCountBubble from './FillsCountBubble';

const Wrap = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
`;

const Text = styled.span`
  text-transform: capitalize;
`;

const getStatusType = (txStatus: ChainTransactionStatus) =>
  txStatus === ChainTransactionStatus.mined
    ? 'success'
    : txStatus === ChainTransactionStatus.failed
    ? 'error'
    : 'info';

type Props = {
  fill: Pick<ApiFill, 'txStatus' | 'txId'>;
  activeChain: Chains;
  /** Number of fills in the aggregation group of this row (see aggregateFillsByOrder) */
  aggregatedFillsCount?: number;
  /** Row is an individual fill of an expanded aggregation group */
  aggregationExpanded?: boolean;
  /** Position of the row within the expanded aggregation group */
  aggregatedFillIndex?: number;
  /** Expand / collapse the aggregation group of this row */
  onToggleAggregation?: () => void;
};

export default function MyTradeClickableStatus({
  fill,
  activeChain,
  aggregatedFillsCount = 1,
  aggregationExpanded = false,
  aggregatedFillIndex,
  onToggleAggregation,
}: Props) {
  const isAggregated = aggregatedFillsCount > 1;

  const status =
    isAggregated && !aggregationExpanded ? (
      // Merged row represents multiple settlement transactions - can't link a single one
      <LabelClickable type={getStatusType(fill.txStatus)}>
        <Text>{readableFillStatus(fill.txStatus)}</Text>
      </LabelClickable>
    ) : fill.txId ? (
      <StopEventPropagationDiv>
        <LabelLink
          type={getStatusType(fill.txStatus)}
          href={getBlockExplorerTransactionUrl(fill.txId, activeChain)}
        >
          <Text>{readableFillStatus(fill.txStatus)}</Text>
        </LabelLink>
      </StopEventPropagationDiv>
    ) : (
      <SimpleRoundedButton disabled>
        <Text>{readableFillStatus(fill.txStatus)}</Text>
      </SimpleRoundedButton>
    );

  if (!isAggregated) {
    return status;
  }

  return (
    <Wrap>
      {status}
      <FillsCountBubble
        aggregatedFillsCount={aggregatedFillsCount}
        aggregationExpanded={aggregationExpanded}
        aggregatedFillIndex={aggregatedFillIndex}
        onToggle={onToggleAggregation}
      />
    </Wrap>
  );
}
